import React from 'react';
import {
  Alert,
  Button,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  getSupportAccessCatalog,
  getSupportAssignments,
  type SupportAssignment,
  type SupportCatalog,
} from '../services/apiService.ts';

const AccessDirectoryPage: React.FC = () => {
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [catalog, setCatalog] = React.useState<SupportCatalog | null>(null);
  const [rows, setRows] = React.useState<SupportAssignment[]>([]);
  const [tenantId, setTenantId] = React.useState('');
  const [locationId, setLocationId] = React.useState('');
  const [userId, setUserId] = React.useState('');

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [nextCatalog, nextRows] = await Promise.all([
        getSupportAccessCatalog(),
        getSupportAssignments({
          tenantId: tenantId || undefined,
          locationId: locationId || undefined,
          userId: userId || undefined,
        }),
      ]);
      setCatalog(nextCatalog);
      setRows(Array.isArray(nextRows) ? nextRows : []);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Nu am putut incarca access directory.');
    } finally {
      setLoading(false);
    }
  }, [locationId, tenantId, userId]);

  React.useEffect(() => {
    load();
  }, [load]);

  return (
    <Stack spacing={3}>
      <Stack spacing={1}>
        <Typography variant="overline" color="text.secondary">
          Internal Access
        </Typography>
        <Typography variant="h4">Access Directory</Typography>
        <Typography color="text.secondary">
          Vizibilitate globala pentru assignment-uri pe locatie, profile si override-uri, fara a intra in 3000.
        </Typography>
      </Stack>

      <Paper sx={{ p: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField
            label="Tenant ID"
            value={tenantId}
            onChange={(event) => setTenantId(event.target.value)}
            size="small"
          />
          <TextField
            label="Location ID"
            value={locationId}
            onChange={(event) => setLocationId(event.target.value)}
            size="small"
          />
          <TextField
            label="User ID"
            value={userId}
            onChange={(event) => setUserId(event.target.value)}
            size="small"
          />
          <Button variant="contained" onClick={load} disabled={loading}>
            {loading ? 'Loading...' : 'Refresh'}
          </Button>
        </Stack>
      </Paper>

      {error ? <Alert severity="error">{error}</Alert> : null}

      {catalog ? (
        <Paper sx={{ p: 3 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
            <Stack spacing={1} sx={{ minWidth: 240 }}>
              <Typography variant="subtitle1">Menus</Typography>
              <Typography variant="h5">{catalog.menus.length}</Typography>
            </Stack>
            <Stack spacing={1} sx={{ minWidth: 240 }}>
              <Typography variant="subtitle1">Profiles</Typography>
              <Typography variant="h5">{catalog.profiles.length}</Typography>
            </Stack>
            <Stack spacing={1} sx={{ minWidth: 240 }}>
              <Typography variant="subtitle1">Assignments</Typography>
              <Typography variant="h5">{rows.length}</Typography>
            </Stack>
          </Stack>
        </Paper>
      ) : null}

      <Paper sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>User</TableCell>
              <TableCell>Tenant</TableCell>
              <TableCell>Location</TableCell>
              <TableCell>Assignment</TableCell>
              <TableCell>Profile</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Overrides</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell>
                  <Stack spacing={0.5}>
                    <Typography fontWeight={700}>
                      {row.userName || row.userEmail || `User #${row.userId ?? '-'}`}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {row.userEmail || '-'}
                    </Typography>
                  </Stack>
                </TableCell>
                <TableCell>{row.tenantName || row.tenantId || '-'}</TableCell>
                <TableCell>{row.locationName || row.locationId || '-'}</TableCell>
                <TableCell>{row.assignmentRoleCode || '-'}</TableCell>
                <TableCell>{row.permissionProfileCode || '-'}</TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1}>
                    {row.primary ? <Chip label="Primary" size="small" color="primary" /> : null}
                    <Chip label={row.status || 'UNKNOWN'} size="small" variant="outlined" />
                  </Stack>
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {(row.allowOverrides || []).map((token) => (
                      <Chip key={`allow-${row.id}-${token}`} label={`ALLOW ${token}`} size="small" />
                    ))}
                    {(row.denyOverrides || []).map((token) => (
                      <Chip key={`deny-${row.id}-${token}`} label={`DENY ${token}`} size="small" color="warning" variant="outlined" />
                    ))}
                    {!row.allowOverrides?.length && !row.denyOverrides?.length ? (
                      <Typography variant="body2" color="text.secondary">
                        none
                      </Typography>
                    ) : null}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
            {!rows.length && !loading ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography color="text.secondary">
                    Nu exista assignment-uri pentru filtrele curente.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </Paper>
    </Stack>
  );
};

export default AccessDirectoryPage;
